import {
  Button,
  Col,
  Form,
  Image,
  Input,
  message,
  Progress,
  Row,
  Select,
  Upload,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import useApi from "../../hooks/useApi";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import { useNavigate } from "react-router-dom";
import generateMediaThumbnail from "browser-thumbnail-generator";

function UploadVideos() {
  const [percent, setPercent] = useState(0);
  const [totalSizeInMB, setTotalSize] = useState(0);
  const [disableForm, setDisableForm] = useState(false);
  const [uploadStarted, setuploadedStarted] = useState(false);
  const [partNumber, SetPartNumber] = useState(0);
  const [subscription_types, setSubscriptionTypes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailURL, setThumbnailURL] = useState(null);
  const [uploadedParts, setUploadedParts] = useState([]);
  const [key, setKey] = useState();
  const uploadId = useRef(null);
  const navigate = useNavigate();
  const [isCompleted, setIsCompleted] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [subjects, setSubjects] = useState([]);
  const [form] = Form.useForm();
  const [selected_subscription_type, setSelectedSubscriptionType] = useState(
    []
  );
  const [startUploadResponse, startUploadRequest] = useApi(
    "/admin/video/start-upload",
    null,
    API_METHODS.POST,
    true
  );
  const [partUploadResponse, partUploadRequest] = useApi(
    "/admin/video/upload-part",
    null,
    API_METHODS.POST,
    true
  );
  const [uploadCompleteResponse, uploadCompleteRequest] = useApi(
    "/admin/video/complete-upload",
    null,
    API_METHODS.POST
  );

  const [subjectResponse, getSubjectRequest] = useApi(
    `/admin/subject/dropdown`,
    null,
    API_METHODS.GET
  );

  const [subscriptionTypesResponse, getSubscriptionTypes] = useApi(
    `/admin/video/subscription-type-dropdown`,
    null,
    API_METHODS.GET
  ); // Only GPAT and Gpat Advance

  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunk size (adjust as needed)

  const handleFileChange = (event) => {
    if (event.file.status == "done") {
      setSelectedFile(event.file.originFileObj);
      setTotalSize(event.file.size / (1024 * 1024));
      setThumbnail(null);
      setThumbnailURL(null);
      form.setFieldValue("thumbnail_second", null);
    } else if (event.file.status == "removed") {
      setSelectedFile(null);
      setTotalSize(0);
      setThumbnail(null);
      setThumbnailURL(null);
      form.setFieldValue("thumbnail_second", null);
    }
  };

  const createThumbnail = (event) => {
    generateMediaThumbnail({
      file: selectedFile,
      width: 200,
      height: 200,
      maintainAspectRatio: true,
      timestamp: event.target.value,
    }).then((response) => {
      const thumbnailUrl = URL.createObjectURL(response.thumbnail);
      setThumbnail(response.thumbnail);
      setThumbnailURL(thumbnailUrl);
    });
  };

  useEffect(() => {
    if (isCompleted) {
      completeMultipartUpload();
    }
  }, [isCompleted]);

  useEffect(() => {
    getSubscriptionTypes();
  }, []);

  useEffect(() => {
    const data =
      subscriptionTypesResponse.data !== BLANK_VALUE
        ? subscriptionTypesResponse.data
        : [];
    setSubscriptionTypes(data);
  }, [subscriptionTypesResponse]);

  useEffect(() => {
    const data =
      subjectResponse.data !== BLANK_VALUE ? subjectResponse.data : [];
    setSubjects(data);
  }, [subjectResponse]);

  useEffect(() => {
    if (startUploadResponse.data) {
      uploadId.current = startUploadResponse.data.UploadId;
      setuploadedStarted(true);
      setKey(startUploadResponse.data.Key);
      const fileReader = new FileReader();

      fileReader.readAsArrayBuffer(selectedFile.slice(0, CHUNK_SIZE));

      fileReader.onload = async (event) => {
        const chunk = event.target.result;
        let currentPartNumber = partNumber + 1;
        SetPartNumber(currentPartNumber);

        const formData = new FormData();
        formData.append("key", startUploadResponse.data.Key);
        formData.append("partNumber", currentPartNumber);
        formData.append("uploadId", uploadId.current);
        formData.append(
          "data",
          new Blob([chunk], { type: "application/octet-stream" })
        );

        partUploadRequest(formData);
      };
    }
  }, [startUploadResponse]);

  useEffect(() => {
    if (uploadCompleteResponse.data) {
      setDisableForm(false);
      messageApi.open({
        type: "success",
        content: "Video added successfully",
      });
      navigate("/portal/videos");
    } else if (uploadCompleteResponse.error) {
      setDisableForm(false);
      messageApi.open({
        type: "error",
        content: uploadCompleteResponse.error,
      });
      navigate("/portal/videos");
    }
  }, [uploadCompleteResponse]);

  useEffect(() => {
    if (partUploadResponse.data && !partUploadResponse.isLoading) {
      setUploadedParts((prev) => [
        ...prev,
        { PartNumber: partNumber, ETag: partUploadResponse.data.ETag },
      ]);

      setPercent(
        (
          ((partNumber * CHUNK_SIZE) / (totalSizeInMB * 1024 * 1024)) *
          100
        ).toFixed(2)
      );
      if (partNumber * CHUNK_SIZE < selectedFile.size) {
        const nextSlice = selectedFile.slice(
          partNumber * CHUNK_SIZE,
          (partNumber + 1) * CHUNK_SIZE
        );
        const fileReader = new FileReader();

        fileReader.readAsArrayBuffer(nextSlice);

        fileReader.onload = async (event) => {
          const chunk = event.target.result;
          let currentPartNumber = partNumber + 1;
          SetPartNumber(currentPartNumber);

          const formData = new FormData();
          formData.append("key", key);
          formData.append("partNumber", currentPartNumber);
          formData.append("uploadId", uploadId.current);
          formData.append(
            "data",
            new Blob([chunk], { type: "application/octet-stream" })
          );

          partUploadRequest(formData);
        };
      } else {
        setPercent(100);
        setIsCompleted(true);
      }
    }
  }, [partUploadResponse]);

  const handleUpload = async (values) => {
    if (!selectedFile) return;
    setDisableForm(true);
    const formData = new FormData();
    formData.append("filename", selectedFile.name);
    formData.append("contentType", selectedFile.type);
    formData.append("subscription_type_id", values.subscription_type_id);
    formData.append("subject_id", values.subject_id);
    formData.append("is_free", values.is_free);
    formData.append("tittle", values.tittle);
    formData.append("thumbnail", thumbnail);

    startUploadRequest(formData);
  };

  const completeMultipartUpload = async () => {
    let data = {
      key: key,
      uploadId: uploadId.current,
      parts: uploadedParts,
    };
    uploadCompleteRequest(data);

    setuploadedStarted(false);
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const subscriptionSelected = (subscription_type_id) => {
    setSelectedSubscriptionType(subscription_type_id);
    if (subscription_type_id == 3) {
      getSubjectRequest();
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        onFinish={handleUpload}
        form={form}
        initialValues={{ is_free: 0 }}
        disabled={disableForm}
      >
        {contextHolder}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="subscription_type_id"
              label="Subscription Type"
              rules={[
                { required: true, message: "Subscription Type is required." },
              ]}
            >
              <Select
                placeholder="Select Subscription Type"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                onSelect={subscriptionSelected}
              >
                {subscription_types?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.subscription_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {selected_subscription_type === 3 && (
            <Col span={8}>
              <Form.Item
                name="subject_id"
                label="Subject Name"
                rules={[
                  { required: true, message: "Subject Name is required." },
                ]}
              >
                <Select
                  placeholder="Select Subject"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {subjects?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.subject_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item
              name="tittle"
              label="Tittle"
              rules={[{ required: true, message: "Tittle is required." }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="is_free"
              label="Is Free Video"
              rules={[
                {
                  required: true,
                  message: "Is Free Video is required.",
                },
              ]}
            >
              <Select
                options={[
                  {
                    value: 1,
                    label: "Yes",
                  },
                  {
                    value: 0,
                    label: "No",
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="video"
              rules={[{ required: true, message: "Video is required." }]}
            >
              <Upload.Dragger
                customRequest={dummyRequest}
                maxCount={1}
                onChange={handleFileChange}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
          {selectedFile && (
            <Col span={24}>
              <Form.Item
                name="thumbnail_second"
                label="Thumbnail Create On"
                rules={[{ required: true, message: "Thumbnail is required." }]}
                onChange={createThumbnail}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row>{thumbnailURL && <Image width={200} src={thumbnailURL} />}</Row>
        <Form.Item style={{ float: "right" }}>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
      {uploadStarted && (
        <Progress
          percent={percent}
          type="line"
          percentPosition={{
            align: "end",
            type: "inner",
          }}
          size={[300, 20]}
        />
      )}
    </>
  );
}

export default UploadVideos;
